import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthContext } from "../../../Context/AuthContext";
import Preloader from "../../../shared/PreLoaders";
import TableComponent from "../../../utils/TableComponent";
import { Authorization } from "../../../Context/Authorization";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const ManageReceipt = () => {
  const [receipt, setReceipt] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { permissions, selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;
  const [vfcOptions, setVfcOptions] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");

  const fetchVfcs = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, config);
      setVfcOptions(response.data.vfcs);
    } catch (error) {
      console.log("Error fetching Data", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcs(selectedJfmcType._id);
  }, [selectedJfmcType]);

  const fetchReceipt = async (e) => {
    e.preventDefault();
    if (!selectedVfc) return;
    setLoading(true);
    try {
      const Config = Authorization();
      const response = await axios.post(`${Api}/api/transactions/getTransactionByVFC`, { vfcId: selectedVfc }, Config);
      const data = response.data || [];
      if (Array.isArray(data)) {
        setReceipt(data.filter((r) => r.type === "R"));
      } else {
        setReceipt([]);
      }
    } catch (error) {
      console.error("Error fetching receipts", error);
      setReceipt([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReceipt = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Receipt Details?")) return;
    try {
      await axios.delete(`${Api}/api/transactions/${id}`);
      setReceipt(receipt.filter((state) => state._id !== id));
      toast.success("Deleted Successfully");
    } catch (err) {
      console.error("Error deleting receipt", err);
      toast.error("Failed to delete receipt");
    }
  };

  const handleEdit = (id) => { navigate(`/manage/receipt/edit/${id}`); };
  const handleAddReceipt = () => { navigate("add/receipt"); };

  const columns = [
    { headerName: "SI.No", field: "index", valueGetter: (params) => params.node.rowIndex + 1 },
    { headerName: "JFMC Name", field: "vfcId.vfcName" },
    { headerName: "Account", field: "accountId.accountName" },
    { headerName: "Voucher Head", field: "voucherType.voucherName" },
    { headerName: "Voucher Number", field: "voucherNumber" },
    { headerName: "Name", field: "partyDetails" },
    { headerName: "Amount", field: "amount" },
    { headerName: "Description", field: "description" },
    { headerName: "Cash/Bank", field: "cashOrBank" },
    { headerName: "Date", field: "date", valueFormatter: (params) => new Date(params.value).toLocaleDateString() },
    {
      headerName: "View Document", field: "viewDocument",
      cellRenderer: (params) => params.data.uploadDocument ? (
        <a href={`${Api}${params.data.uploadDocument}`} target="_blank" rel="noopener noreferrer">View Document</a>
      ) : "No document",
    },
    {
      headerName: "Action", field: "action",
      cellRenderer: (params) => (
        <div>
          {permissions.includes("EditReceipt") && (
            <i className="mdi mdi-pen" style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => { console.log("Navigating to edit:", params.data._id); handleEdit(params.data._id); }} />
          )}
          {permissions.includes("DeleteReceipt") && (
            <i className="mdi mdi-delete" style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleDeleteReceipt(params.data._id)} />
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>All Receipts</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Transactions</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Manage Receipts</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Select JFMC</span>
            </div>
            <form onSubmit={fetchReceipt}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>JFMC Name</label>
                  <select value={selectedVfc} onChange={(e) => setSelectedVfc(e.target.value)} style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                    <option value="">Select JFMC</option>
                    {vfcOptions.sort((a, b) => a.vfcName.localeCompare(b.vfcName)).map((vfc) => (
                      <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>
                    ))}
                  </select>
                </div>
                <button type="submit"
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", whiteSpace: "nowrap" }}>
                  <i className="mdi mdi-file-check" /> Submit
                </button>
              </div>
            </form>
          </div>

          {/* ── Results Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
              <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                <i className="mdi mdi-table-large" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Receipt Records</span>
              {permissions.includes("AddReceipt") && (
                <button type="button" onClick={handleAddReceipt}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  <i className="mdi mdi-plus" /> Create New
                </button>
              )}
            </div>
            <div style={{ padding: 16 }}>
              <TableComponent data={receipt} columns={columns} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageReceipt;
